import { useState } from "react";
import { MailOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Col, Form, Input, Modal, Popconfirm, Row, Typography } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { RegisteredStudentData } from "./AdminSingleEventPage";

const { Text } = Typography;

interface Props {
  eventId: number;
  registeredStudents: RegisteredStudentData[];
}

const SendEmailModal: React.FC<Props> = ({ eventId, registeredStudents }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [form] = Form.useForm();
  const [onlyAttended, setOnlyAttended] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Watch form values to determine if send button should be disabled
  const subject = Form.useWatch("subject", form);
  const content = Form.useWatch("content", form);

  // Helper function to check if ReactQuill content has text (removes HTML tags)
  const hasTextContent = (htmlContent: string | undefined): boolean => {
    if (!htmlContent) return false;
    // Remove HTML tags and check if there's actual text content
    const textContent = htmlContent.replace(/<[^>]*>/g, "").trim();
    return textContent.length > 0;
  };

  const getRecipientCount = () => {
    if (onlyAttended) {
      return registeredStudents.filter(s => s.time_attended).length;
    }
    return registeredStudents.length;
  };

  const recipientCount = getRecipientCount();
  const isFormValid = subject && subject.trim() !== "" && hasTextContent(content) && recipientCount > 0;

  const handlePreview = async () => {
    try {
      const values = await form.validateFields(["subject", "content"]);
      setLoading(true);
      const response = await axios.post(`/events/${eventId}/preview-email/`, {
        subject: values.subject,
        content: values.content,
      });
      setPreviewHtml(response.data.html);
      setIsPreviewVisible(true);
    } catch (error) {
      if (error && typeof error === "object" && "errorFields" in error) {
        // Form validation error
        return;
      }
      message("error", "Kunne ikke laste forhåndsvisning");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await axios.post(`/events/${eventId}/send-email/`, {
        subject: values.subject,
        content: values.content,
        only_attended: onlyAttended,
        sender_email: "no-reply@bindeleddet.no",
      });
      message("success", "E-post sendt til alle mottakere");
      setIsModalVisible(false);
      form.resetFields();
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      message("error", errorMessage || "Kunne ikke sende e-post");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="primary"
        title="Send e-post til påmeldte"
        onClick={() => setIsModalVisible(true)}
        style={{ marginRight: "5px" }}
      >
        <MailOutlined />
      </Button>
      <Modal
        title="Send e-post til påmeldte"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setOnlyAttended(false);
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Alert
            message="E-posten sendes fra no-reply@bindeleddet.no"
            type="info"
            showIcon
            style={{ marginBottom: "16px" }}
          />

          <Form.Item label="Tittel" name="subject" rules={[{ required: true, message: "Tittel er påkrevd" }]}>
            <Input placeholder="E-post tittel" />
          </Form.Item>

          <Form.Item
            label="Innhold"
            name="content"
            rules={[{ required: true, message: "Innhold er påkrevd" }]}
            getValueFromEvent={value => value}
          >
            <ReactQuill theme="snow" placeholder="Skriv e-post innhold her..." />
          </Form.Item>

          <Form.Item>
            <Checkbox checked={onlyAttended} onChange={e => setOnlyAttended(e.target.checked)}>
              Send kun til studenter med registrert oppmøte
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Text>
              <strong>{recipientCount}</strong> studenter vil motta denne e-posten
            </Text>
          </Form.Item>

          <Form.Item>
            <Row gutter={8}>
              <Col>
                <Button onClick={handlePreview} loading={loading} disabled={!isFormValid}>
                  Forhåndsvisning
                </Button>
              </Col>
              <Col>
                <Popconfirm
                  title={
                    <>
                      <div>Er du sikker på at du vil sende e-post?</div>
                      <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                        Dette vil sende e-post til {recipientCount} studenter.
                      </div>
                    </>
                  }
                  onConfirm={handleSend}
                  okText="Send"
                  cancelText="Avbryt"
                  okType="primary"
                  disabled={!isFormValid}
                >
                  <Button type="primary" loading={loading} disabled={!isFormValid}>
                    Send
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Forhåndsvisning av e-post"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsPreviewVisible(false)}>
            Lukk
          </Button>,
        ]}
        width={800}
      >
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </Modal>
    </>
  );
};

export default SendEmailModal;
